/**
 * PartnerHubs - the partner's physical depots: where riders start a shift,
 * where stock is staged, and how much each place can hold.
 *
 * WHY THIS SCREEN EXISTS
 * A hub carries the two facts nobody can guess from an order: the exact point
 * a rider is dispatched from, and the window in which that place is actually
 * open. When they are missing or stale, the dispatch desk plans against a hub
 * that closed at 18:00 and the delivery quietly fails in the evening. The list
 * is short lived knowledge held in someone's head today, so the screen's job
 * is to get it written down and keep it visible.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getUserFromLocalStorage } from "../../utilities/user";
import { apiFetchPartnerHubs, apiAddPartnerHub } from "../../api/index.js";
import LocationPicker from "../../components/LocationPicker";
import {
  colors,
  font,
  text,
  card,
  cardBody,
  grid,
  inp,
  button,
  pill,
} from "./partnerTheme";
import PartnerHelp from "./partnerHelp";

/* Every 3PL endpoint answers failures in the same envelope, and the server
   sentence names the field that was refused, which a generic message does
   not. */
const serverMessage = (err, fallback) =>
  err?.response?.data?.error?.message || fallback;

/* The schema defaults, restated so the form can show what the server falls
   back to on a blank box. Keep in step with hubSchema in
   models/ThreePLPartnerModel.js. */
const DEFAULTS = {
  capacity: 100,
  open: "06:00",
  close: "22:00",
};

const EMPTY_FORM = {
  name: "",
  street: "",
  city: "",
  district: "",
  lat: "",
  lng: "",
  capacity: "",
  open: "",
  close: "",
};

/* A blank box means "let the server apply its default", which is not zero, and
   Number("") is 0, so the blank is caught before the cast. */
const numberOrDefault = (raw, fallback) =>
  String(raw).trim() === "" ? fallback : Number(raw);

const textOrDefault = (raw, fallback) =>
  String(raw).trim() === "" ? fallback : String(raw).trim();

const Field = ({ label, help, children }) => (
  <div style={{ marginBottom: 18 }}>
    <span style={text.label}>{label}</span>
    {children}
    {help ? <p style={text.help}>{help}</p> : null}
  </div>
);

/* Address parts are all optional on the schema, so a hub can legitimately come
   back with only some of them. Joining what exists beats printing "undefined"
   down a column. */
const formatAddress = (address) => {
  const parts = [address?.street, address?.city, address?.district].filter(
    (p) => typeof p === "string" && p.trim() !== ""
  );
  return parts.length ? parts.join(", ") : "";
};

const PartnerHubs = () => {
  const { agent } = useSelector((state) => state.auth);
  const user = agent || getUserFromLocalStorage();
  const partnerId = user?.result?.partnerId || user?.result?._id;
  const operationMode = user?.result?.operationMode;
  const isPartnerAccount = operationMode === "3PL_PARTNER";
  const permissions = user?.result?.permissions || {};

  /* The server re-checks access on the write, so this gate is not the security
     boundary. It only avoids offering a form whose one outcome would be a
     refusal. A hub is where riders are dispatched from, so it rides on fleet
     access rather than financial access. */
  // Must match the server: POST /:id/hubs is checkStaffPermission("profile", "full").
  const canAddHub = isPartnerAccount || permissions?.profile === "full";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hubs, setHubs] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const setField = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  /* The hub point is still held as the two strings every other box holds, so
     readCoordinates and the payload below are untouched. The map just writes
     both of them at once, and only ever writes a whole pair. */
  const point =
    String(form.lat).trim() !== "" && String(form.lng).trim() !== ""
      ? { lat: Number(form.lat), lng: Number(form.lng) }
      : null;

  const setPoint = (next) =>
    setForm((prev) => ({
      ...prev,
      lat: next && Number.isFinite(next.lat) ? String(next.lat) : "",
      lng: next && Number.isFinite(next.lng) ? String(next.lng) : "",
    }));

  const load = useCallback(async () => {
    if (!partnerId) {
      setLoading(false);
      setError("No partner account is attached to this login, so there are no hubs to show.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const res = await apiFetchPartnerHubs(partnerId);
      if (res?.data?.success) {
        const list = res.data.data?.hubs;
        setHubs(Array.isArray(list) ? list : []);
      } else {
        setError("The hubs could not be loaded.");
      }
    } catch (err) {
      setError(serverMessage(err, "The hubs could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    load();
  }, [load]);

  /* Latitude and longitude are the only inputs that fail silently rather than
     loudly: a swapped pair still saves, and the only symptom is riders being
     routed to a point in the wrong country. So they are checked here, in
     words, before anything is sent. */
  const readCoordinates = () => {
    const lat = Number(form.lat);
    const lng = Number(form.lng);

    if (String(form.lat).trim() === "" || String(form.lng).trim() === "") {
      return { ok: false, message: "Enter both the latitude and the longitude of the hub." };
    }
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return {
        ok: false,
        message: "Latitude and longitude have to be plain numbers, for example 0.3476 and 32.5825.",
      };
    }
    if (lat < -90 || lat > 90) {
      return { ok: false, message: "Latitude has to be between -90 and 90. Check you have not typed the longitude here." };
    }
    if (lng < -180 || lng > 180) {
      return { ok: false, message: "Longitude has to be between -180 and 180. Check you have not typed the latitude here." };
    }
    return { ok: true, lat, lng };
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      toast.error("Give the hub a name, for example Nakawa Depot.");
      return;
    }

    const coords = readCoordinates();
    if (!coords.ok) {
      toast.error(coords.message);
      return;
    }

    const capacity = numberOrDefault(form.capacity, DEFAULTS.capacity);
    if (!Number.isFinite(capacity) || capacity < 0) {
      toast.error("Capacity has to be a number, and it cannot be negative.");
      return;
    }

    const open = textOrDefault(form.open, DEFAULTS.open);
    const close = textOrDefault(form.close, DEFAULTS.close);

    try {
      setSaving(true);
      /* A hub's coordinates are a single point on the schema, not the array a
         service area uses, so one lat/lng pair is sent as one object. */
      const res = await apiAddPartnerHub(partnerId, {
        name: form.name.trim(),
        address: {
          street: form.street.trim(),
          city: form.city.trim(),
          district: form.district.trim(),
        },
        coordinates: { lat: coords.lat, lng: coords.lng },
        capacity,
        operatingHours: { open, close },
      });

      if (res?.data?.success) {
        const created = res.data.data?.hub;
        if (created) {
          setHubs((prev) => [...prev, created]);
        } else {
          load();
        }
        setForm(EMPTY_FORM);
        setShowForm(false);
        toast.success("Hub added.");
      } else {
        toast.error("The hub was not saved.");
      }
    } catch (err) {
      toast.error(serverMessage(err, "The hub was not saved."));
    } finally {
      setSaving(false);
    }
  };

  const th = {
    ...text.label,
    display: "table-cell",
    marginBottom: 0,
    padding: "10px 14px",
    textAlign: "left",
    borderBottom: `1px solid ${colors.line}`,
    whiteSpace: "nowrap",
  };
  const td = {
    ...text.body,
    padding: "12px 14px",
    borderBottom: `1px solid ${colors.lineSoft}`,
    verticalAlign: "top",
  };
  const numCell = { ...td, ...text.numeric, padding: "12px 14px" };

  if (loading) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, textAlign: "center" }}>
          <p style={{ ...text.body, margin: 0, color: colors.muted }}>Loading your hubs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <div style={{ ...card, ...cardBody, borderColor: colors.red }}>
          <h4 style={{ ...text.title, color: colors.red, marginBottom: 6 }}>Hubs could not load</h4>
          <p style={{ ...text.body, margin: "0 0 16px" }}>{error}</p>
          <button type="button" style={button("secondary")} onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      {/* Header */}
      <div
        style={{
          ...card,
          ...cardBody,
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <div style={{ minWidth: 240, flex: "1 1 320px" }}>
          <h4 style={text.title}>
            Hubs
            <PartnerHelp topic="hubsRecorded" />
          </h4>
          <p style={text.subtitle}>
            The depots your riders work out of, with the hours each one is open and how much it can
            hold.
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" style={button("secondary")} onClick={load}>
            Refresh
          </button>
          {canAddHub && hubs.length > 0 && (
            <button
              type="button"
              style={button(showForm ? "quiet" : "primary")}
              onClick={() => setShowForm((v) => !v)}
            >
              {showForm ? "Cancel" : "Add hub"}
            </button>
          )}
        </div>
      </div>

      {/* The list, or the reason there is nothing in it */}
      <div style={{ ...card, ...cardBody }}>
        <p style={text.section}>{hubs.length === 1 ? "1 hub" : `${hubs.length} hubs`}</p>

        {hubs.length === 0 ? (
          <div style={{ padding: "28px 0", textAlign: "center" }}>
            <span style={{ ...pill("warn"), marginBottom: 12 }}>No dispatch point on record</span>
            <p style={{ ...text.body, margin: "0 0 6px", fontWeight: 600, color: colors.ink }}>
              You have no hubs yet.
            </p>
            <p style={{ ...text.body, margin: "0 auto 16px", color: colors.muted, maxWidth: 520 }}>
              Without a hub there is no recorded point your riders start from and no opening hours to
              plan against, so the dispatch desk has to guess both.
            </p>
            {canAddHub ? (
              <button type="button" style={button("primary")} onClick={() => setShowForm(true)}>
                Add your first hub
              </button>
            ) : (
              <p style={{ ...text.help, maxWidth: 420, margin: "0 auto" }}>
                Ask the partner owner, or staff with full rider access, to add one.
              </p>
            )}
          </div>
        ) : (
          /* Its own scroller: six columns would otherwise drag the whole page
             sideways on a laptop window. */
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 880, borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>Hub</th>
                  <th style={th}>Address</th>
                  <th style={th}>Coordinates</th>
                  <th style={th}>Capacity</th>
                  <th style={th}>Operating hours</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {hubs.map((hub, index) => {
                  const address = formatAddress(hub.address);
                  const hours = hub.operatingHours || {};
                  const point = hub.coordinates;
                  return (
                    <tr key={hub._id || `${hub.name}-${index}`}>
                      <td style={td}>
                        <div style={{ fontWeight: 600, color: colors.ink }}>
                          {hub.name || "Unnamed hub"}
                        </div>
                      </td>
                      <td style={td}>
                        {address ? (
                          address
                        ) : (
                          <span style={{ ...text.help, margin: 0 }}>No address recorded</span>
                        )}
                      </td>
                      <td style={td}>
                        {point && Number.isFinite(Number(point.lat)) && Number.isFinite(Number(point.lng)) ? (
                          <span style={{ fontFamily: font.mono, fontSize: 12, color: colors.body }}>
                            {point.lat}, {point.lng}
                          </span>
                        ) : (
                          <span style={pill("bad")}>No coordinates</span>
                        )}
                      </td>
                      <td style={numCell}>{Number(hub.capacity || 0)}</td>
                      <td style={numCell}>
                        {hours.open || DEFAULTS.open} to {hours.close || DEFAULTS.close}
                      </td>
                      <td style={td}>
                        <span style={pill(hub.isActive === false ? "muted" : "good")}>
                          {hub.isActive === false ? "Inactive" : "Active"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add form */}
      {canAddHub && showForm && (
        <form style={{ ...card, ...cardBody }} onSubmit={submit}>
          <p style={text.section}>Add a hub</p>

          <div style={{ ...grid(2, 20), gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
            <Field label="Hub name" help="What your team calls it, for example Nakawa Depot.">
              <input
                type="text"
                value={form.name}
                onChange={setField("name")}
                placeholder="Nakawa Depot"
                {...inp()}
              />
            </Field>

            <Field label="Capacity" help={`Orders the hub can hold at once. Default ${DEFAULTS.capacity}.`}>
              <input
                type="number"
                min="0"
                step="10"
                value={form.capacity}
                onChange={setField("capacity")}
                placeholder={String(DEFAULTS.capacity)}
                {...inp()}
              />
            </Field>

            <Field label="Street" help="Optional. The line a rider would read off a sign.">
              <input
                type="text"
                value={form.street}
                onChange={setField("street")}
                placeholder="Plot 14, Jinja Road"
                {...inp()}
              />
            </Field>

            <Field label="City" help="Optional.">
              <input
                type="text"
                value={form.city}
                onChange={setField("city")}
                placeholder="Kampala"
                {...inp()}
              />
            </Field>

            <Field label="District" help="Optional.">
              <input
                type="text"
                value={form.district}
                onChange={setField("district")}
                placeholder="Nakawa"
                {...inp()}
              />
            </Field>

            {/* Full width: a map squeezed into one column of a two column
                grid is too small to aim with, which defeats the point of it. */}
            <div style={{ gridColumn: "1 / -1", marginBottom: 18 }}>
              <LocationPicker
                label="Hub location"
                help="Search for the depot, click the map, or drag the pin. This is the exact point riders are dispatched from. Kampala sits near 0.3476, 32.5825."
                value={point}
                onChange={setPoint}
              />
            </div>

            <Field label="Opens" help={`Leave blank for the default of ${DEFAULTS.open}.`}>
              <input
                type="time"
                value={form.open}
                onChange={setField("open")}
                placeholder={DEFAULTS.open}
                {...inp()}
              />
            </Field>

            <Field label="Closes" help={`Leave blank for the default of ${DEFAULTS.close}.`}>
              <input
                type="time"
                value={form.close}
                onChange={setField("close")}
                placeholder={DEFAULTS.close}
                {...inp()}
              />
            </Field>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6 }}>
            <button
              type="submit"
              style={button("primary", saving ? { opacity: 0.6, cursor: "wait" } : {})}
              disabled={saving}
            >
              {saving ? "Saving" : "Save hub"}
            </button>
            <button
              type="button"
              style={button("quiet")}
              disabled={saving}
              onClick={() => {
                setForm(EMPTY_FORM);
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default PartnerHubs;
